import { hc } from "@/frontend/lib/hc";

// Type for member onboarding status response
interface MemberOnboardingStatus {
  onboardingCompleted: boolean;
  role: string;
  /** The workspace's no-card trial ended without a paid plan */
  billingLocked?: boolean;
  /** Locked from the start: the email that created the workspace already used the free trial */
  trialAlreadyUsed?: boolean;
}

/**
 * Check if a member has completed onboarding for a specific org.
 * Returns true if onboarding is complete, false if not.
 *
 * @param orgId - The organization ID to check
 * @param defaultOnError - What to return on error:
 *   - true: Assume completed (use in Login.tsx to avoid blocking existing users)
 *   - false: Assume not completed (use in NameStep.tsx to allow onboarding)
 */
export async function checkMemberOnboardingStatus(
  orgId: string,
  defaultOnError: boolean = false
): Promise<boolean> {
  try {
    const response = await hc.api.member[":orgId"]["onboarding-status"].$get({
      param: { orgId },
    });

    if (response.ok) {
      const data: MemberOnboardingStatus = await response.json();
      return data.onboardingCompleted;
    }
  } catch (err) {
    console.error("Error checking member onboarding status:", err);
  }
  return defaultOnError;
}

/**
 * Get the full member onboarding status for a specific org.
 * Returns { onboardingCompleted, role } or null on error.
 */
export async function getMemberOnboardingStatus(
  orgId: string
): Promise<MemberOnboardingStatus | null> {
  try {
    const response = await hc.api.member[":orgId"]["onboarding-status"].$get({
      param: { orgId },
    });
    if (response.ok) {
      return (await response.json()) as MemberOnboardingStatus;
    }
  } catch (err) {
    console.error("Error checking member onboarding status:", err);
  }
  return null;
}
